import { Link } from "react-router-dom";
import { SITE, HOW_IT_WORKS, TESTIMONIALS } from "@/lib/site";
import { HOME_FAQS } from "@/lib/faq";
import { AREAS } from "@/lib/areas";
import TrustStrip from "@/components/TrustStrip";
import AwardsStrip from "@/components/AwardsStrip";
import EnquiryForm from "@/components/EnquiryForm";
import CTABanner from "@/components/CTABanner";
import FAQSection from "@/components/FAQSection";

const HomePage = () => {
  return (
    <main data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[var(--ss-teal-700)] text-[var(--ss-cream)] grain">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -left-24 w-[500px] h-[500px] rounded-full bg-[var(--ss-teal-600)]/50 blur-3xl"></div>
          <div className="absolute -bottom-32 -right-24 w-[600px] h-[600px] rounded-full bg-[var(--ss-gold)]/10 blur-3xl"></div>
        </div>

        <div className="container-px max-w-7xl mx-auto relative z-10 pt-10 md:pt-14 pb-24 md:pb-28">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
            {/* Copy */}
            <div className="lg:col-span-7">
              <span className="eyebrow eyebrow-on-dark reveal">
                Premium Live-In Care · Essex
              </span>
              <h1 className="font-display text-5xl md:text-6xl xl:text-7xl mt-4 reveal reveal-delay-1">
                The same trusted carer.{" "}
                <em className="not-italic text-[var(--ss-cream)] underline decoration-[var(--ss-gold)] decoration-[3px] underline-offset-[10px]">In their own home.</em>{" "}
                Every single day.
              </h1>
              <p className="mt-5 text-base md:text-lg text-[var(--ss-cream)]/85 max-w-xl reveal reveal-delay-2">
                A local, family-run alternative to a care home — for the parent
                you'd do anything for. Bespoke live-in and overnight care across
                Epping Forest, Harlow and Bishop's Stortford, rated <span className="text-[var(--ss-gold)] font-semibold">Good</span> by the CQC.
              </p>

              <div className="mt-6 flex flex-col sm:flex-row gap-3 reveal reveal-delay-3">
                <Link to="/contact#enquiry-form" data-testid="hero-cta-book" className="btn btn-gold">
                  <i className="fa-regular fa-calendar-check"></i>
                  Book a free assessment
                </Link>
                <a href={SITE.phoneHref} data-testid="hero-cta-call" className="btn btn-outline-light">
                  <i className="fa-solid fa-phone"></i>
                  Call {SITE.phone}
                </a>
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[var(--ss-cream)]/80 reveal reveal-delay-4">
                <span><i className="fa-solid fa-award text-[var(--ss-gold)] mr-2"></i> CQC Rated Good</span>
                <span><i className="fa-solid fa-shield-halved text-[var(--ss-gold)] mr-2"></i> UKHCA Member</span>
                <span><i className="fa-solid fa-hand-holding-heart text-[var(--ss-gold)] mr-2"></i> Family-run</span>
                <span><i className="fa-solid fa-clock text-[var(--ss-gold)] mr-2"></i> 24/7 care line</span>
              </div>
            </div>

            {/* Visual side card */}
            <div className="lg:col-span-5 reveal reveal-delay-2">
              <div className="relative">
                <div className="absolute -inset-4 rounded-[28px] bg-[var(--ss-gold)]/15 blur-2xl"></div>
                <div className="relative bg-[var(--ss-cream)] text-[var(--ss-ink)] rounded-[24px] p-7 md:p-8 shadow-[0_30px_70px_-30px_rgba(0,0,0,0.5)]">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[var(--ss-teal-700)] text-[var(--ss-cream)] flex items-center justify-center">
                      <i className="fa-regular fa-comments"></i>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--ss-ink-soft)]">
                        Free, no-pressure
                      </div>
                      <div className="font-serif text-2xl text-[var(--ss-teal-800)]">
                        Speak to a real carer today
                      </div>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-[var(--ss-ink-soft)]">
                    Tell us what's worrying you. We'll listen, share what we'd
                    do — and call back when it suits you.
                  </p>
                  <div className="mt-5">
                    <EnquiryForm sourcePage="home-hero" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* curve to next section */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="w-full h-[44px] block">
            <path d="M0,40 C240,10 480,60 720,30 C960,0 1200,50 1440,20 L1440,60 L0,60 Z" fill="var(--ss-paper)"/>
          </svg>
        </div>
      </section>

      {/* TRUST */}
      <section className="container-px max-w-7xl mx-auto -mt-10 relative z-10">
        <TrustStrip />
      </section>

      {/* AWARDS */}
      <section className="py-16 md:py-20">
        <div className="container-px max-w-6xl mx-auto">
          <AwardsStrip />
        </div>
      </section>

      {/* LIVE-IN HERO SERVICE */}
      <section className="section">
        <div className="container-px max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="relative rounded-[28px] overflow-hidden aspect-[4/5] shadow-xl">
              <img
                alt="An elderly mother smiling warmly as her daughter holds her hands"
                className="w-full h-full object-cover"
                src="https://images.pexels.com/photos/3768114/pexels-photo-3768114.jpeg?auto=compress&cs=tinysrgb&w=1200"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--ss-teal-900)]/40 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6 bg-[var(--ss-cream)] rounded-2xl p-5 flex items-center gap-4 shadow-lg">
                <div className="w-12 h-12 rounded-full bg-[var(--ss-gold)] text-[var(--ss-teal-900)] flex items-center justify-center">
                  <i className="fa-solid fa-heart"></i>
                </div>
                <div>
                  <div className="font-serif text-xl text-[var(--ss-teal-800)] leading-tight">
                    The right alternative to a care home
                  </div>
                  <div className="text-xs text-[var(--ss-ink-soft)] mt-1">
                    96% of people prefer to stay in their own home
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <span className="eyebrow">Our flagship service</span>
            <h2 className="font-display text-4xl md:text-6xl mt-4 text-[var(--ss-teal-800)]">
              Live-in care that feels like family.
            </h2>
            <p className="mt-5 text-lg text-[var(--ss-ink-soft)] max-w-xl">
              One dedicated carer, in their own home, 24 hours a day. Familiar
              routines kept, favourite chair kept, the cat kept — and a
              consistent face who genuinely gets to know your loved one.
            </p>

            <ul className="mt-7 space-y-3 text-[var(--ss-ink)]">
              {[
                "Round-the-clock companionship and personal care",
                "Matched on personality, hobbies and lifestyle — not just availability",
                "Your loved one stays in the home they love, with their things, their pets, their garden",
                "A senior carer is your single, named point of contact — always",
              ].map((p) => (
                <li key={p} className="flex gap-3">
                  <span className="mt-1 w-6 h-6 rounded-full bg-[var(--ss-gold)]/20 text-[var(--ss-gold-dark)] flex items-center justify-center text-xs">
                    <i className="fa-solid fa-check"></i>
                  </span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/live-in-care" className="btn btn-primary" data-testid="home-livein-learn">
                Learn about live-in care
                <i className="fa-solid fa-arrow-right"></i>
              </Link>
              <a href={SITE.phoneHref} className="btn btn-outline-dark" data-testid="home-livein-call">
                <i className="fa-solid fa-phone"></i>
                Speak to our team
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section bg-[var(--ss-cream)]">
        <div className="container-px max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <span className="eyebrow">How it works</span>
            <h2 className="font-display text-4xl md:text-6xl mt-4 text-[var(--ss-teal-800)]">
              From the first phone call to a carer at the door.
            </h2>
            <p className="mt-4 text-lg text-[var(--ss-ink-soft)]">
              Arranging care for a parent shouldn't feel like a maze. Here's the
              gentle, four-step path we'll walk with you.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {HOW_IT_WORKS.map((s, i) => (
              <div key={s.step} className="card-soft relative" data-testid={`how-step-${i + 1}`}>
                <div className="font-serif text-5xl text-[var(--ss-gold)] leading-none">
                  {s.step}
                </div>
                <h3 className="font-serif text-2xl text-[var(--ss-teal-800)] mt-4">
                  {s.title}
                </h3>
                <p className="mt-3 text-[var(--ss-ink-soft)] text-sm leading-relaxed">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY SEASHELL */}
      <section className="section">
        <div className="container-px max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <span className="eyebrow">Why SeaShells</span>
              <h2 className="font-display text-4xl md:text-6xl mt-4 text-[var(--ss-teal-800)]">
                Big enough to be safe.<br className="hidden md:block" />
                Small enough to remember your dog's name.
              </h2>
            </div>
            <div className="lg:col-span-5 text-[var(--ss-ink-soft)]">
              We started SeaShells because we wanted the kind of care we'd want
              for our own parents — and couldn't find it. So we built it.
            </div>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: "fa-hand-holding-heart", title: "Family-run", body: "Founded by carers, owned by a family, never absorbed into a faceless chain." },
              { icon: "fa-user-check", title: "Same carer, every visit", body: "We refuse rota-roulette. The same warm face, building real trust." },
              { icon: "fa-award", title: "CQC Rated Good", body: "Independently inspected. We hold ourselves to a higher standard than required." },
              { icon: "fa-location-dot", title: "Truly local", body: "We only cover Epping Forest, Harlow and Bishop's Stortford — so we know the streets, not just the postcodes." },
            ].map((f, i) => (
              <div key={f.title} className="card-soft" data-testid={`why-${i + 1}`}>
                <div className="w-12 h-12 rounded-full bg-[var(--ss-cream)] text-[var(--ss-teal-700)] flex items-center justify-center text-xl">
                  <i className={`fa-solid ${f.icon}`}></i>
                </div>
                <h3 className="font-serif text-xl text-[var(--ss-teal-800)] mt-5">
                  {f.title}
                </h3>
                <p className="mt-2 text-sm text-[var(--ss-ink-soft)] leading-relaxed">
                  {f.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section bg-[var(--ss-teal-800)] text-[var(--ss-cream)] relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[var(--ss-teal-700)]/60 blur-3xl"></div>
        <div className="container-px max-w-7xl mx-auto relative z-10">
          <div className="max-w-2xl">
            <span className="eyebrow eyebrow-on-dark">Families we've cared for</span>
            <h2 className="font-display text-4xl md:text-6xl mt-4">
              "We finally feel like we're not doing this alone."
            </h2>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <figure
                key={t.name}
                data-testid={`testimonial-${i + 1}`}
                className="bg-[var(--ss-teal-900)]/60 border border-[var(--ss-cream)]/10 rounded-2xl p-7 backdrop-blur"
              >
                <div className="text-[var(--ss-gold)] text-xl">
                  {"★★★★★".split("").map((s, idx) => (
                    <span key={idx}>{s}</span>
                  ))}
                </div>
                <blockquote className="font-serif italic text-xl mt-5 leading-snug">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-6 text-sm">
                  <div className="font-semibold">{t.name}</div>
                  <div className="text-[var(--ss-cream)]/60">{t.role}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* SECONDARY SERVICES */}
      <section className="section">
        <div className="container-px max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <span className="eyebrow">More than live-in</span>
            <h2 className="font-display text-4xl md:text-6xl mt-4 text-[var(--ss-teal-800)]">
              Other ways we can help.
            </h2>
            <p className="mt-4 text-lg text-[var(--ss-ink-soft)]">
              Sometimes families need a gentler step. We offer flexible support
              that grows with you.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {[
              {
                to: "/overnight-care",
                tag: "Overnight",
                title: "Peace of mind, through the night.",
                body: "A discreet, awake carer in the home from evening through morning — so you can sleep, knowing they're safe.",
                icon: "fa-moon",
              },
              {
                to: "/visiting-care",
                tag: "Visiting",
                title: "Hourly visits, the way they like things done.",
                body: "From a 30-minute companionship call to multiple daily visits — built around their routine, not ours.",
                icon: "fa-mug-hot",
              },
            ].map((s, i) => (
              <Link
                key={s.to}
                to={s.to}
                data-testid={`secondary-service-${i + 1}`}
                className="group relative rounded-3xl overflow-hidden border border-[var(--ss-line)] bg-white p-8 md:p-10 hover:border-[var(--ss-gold)] transition-colors"
              >
                <div className="flex items-center gap-3 text-[var(--ss-gold-dark)]">
                  <span className="w-12 h-12 rounded-full bg-[var(--ss-cream)] flex items-center justify-center text-xl">
                    <i className={`fa-solid ${s.icon}`}></i>
                  </span>
                  <span className="text-xs uppercase tracking-[0.25em]">{s.tag}</span>
                </div>
                <h3 className="font-display text-3xl md:text-4xl mt-6 text-[var(--ss-teal-800)]">
                  {s.title}
                </h3>
                <p className="mt-4 text-[var(--ss-ink-soft)]">{s.body}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-[var(--ss-teal-700)] font-semibold">
                  Learn more
                  <i className="fa-solid fa-arrow-right transition-transform group-hover:translate-x-1"></i>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT IS LIVE-IN CARE FOR THE ELDERLY — SEO content block */}
      <section className="section bg-[var(--ss-cream-soft)]">
        <div className="container-px max-w-5xl mx-auto grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <span className="eyebrow">In plain English</span>
            <h2 className="font-display text-4xl md:text-5xl mt-4 text-[var(--ss-teal-800)]">
              What is live-in care for the elderly?
            </h2>
          </div>
          <div className="lg:col-span-7 text-[var(--ss-ink-soft)] text-lg leading-relaxed space-y-4">
            <p>
              <strong className="text-[var(--ss-teal-800)]">Live-in care for elders</strong> is exactly what it sounds like — a
              fully trained <strong className="text-[var(--ss-teal-800)]">live-in carer for elderly</strong> people
              moves into your loved one's home and is there round the clock,
              every day.
            </p>
            <p>
              It's the most personal form of <strong className="text-[var(--ss-teal-800)]">in-home care for elders</strong> we
              offer. Instead of moving an ageing parent into a care home — with
              new staff on every shift and shared rooms — the support comes to
              them, in the home they know. Familiar surroundings stay; the
              garden stays; the cat stays; the photographs on the mantelpiece
              stay. The only thing that changes is that someone caring,
              competent and kind is there when needed.
            </p>
            <p>
              SeaShells provides this kind of premium, one-to-one live-in care
              across Epping Forest, Loughton, Theydon Bois, Harlow and the
              surrounding Essex villages. It's CQC-regulated, family-run, and
              quietly recommended by the families we already look after.
            </p>
            <div className="pt-2">
              <Link
                to="/live-in-care"
                data-testid="seo-block-cta"
                className="btn btn-primary"
              >
                Read more about live-in care
                <i className="fa-solid fa-arrow-right"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQSection
        eyebrow="Live-in care · FAQs"
        title="Questions families ask before they call us."
        intro="The questions we hear most often from adult children weighing up live-in care for an elderly parent. Got a different one? Pick up the phone — we'll answer it the same way."
        items={HOME_FAQS}
        testIdPrefix="home-faq"
      />

      {/* AREAS WE SERVE — local SEO block */}
      <section className="section bg-[var(--ss-teal-800)] text-[var(--ss-cream)]">
        <div className="container-px max-w-7xl mx-auto text-center">
          <span className="eyebrow eyebrow-on-dark">Where we work</span>
          <h2 className="font-display text-4xl md:text-5xl mt-4">
            Premium live-in care across Epping Forest &amp; Harlow.
          </h2>
          <p className="mt-4 text-[var(--ss-cream)]/80 max-w-2xl mx-auto">
            We're deliberately local. We only deliver in-home care for elders in
            the towns and villages we know — because knowing the street, the GP
            surgery, and the local pharmacy matters.
          </p>
          <ul className="mt-10 flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {[
              "Epping", "Loughton", "Theydon Bois", "Buckhurst Hill",
              "Chigwell", "Ongar", "North Weald", "Harlow",
              "Old Harlow", "Roydon", "Nazeing", "Waltham Abbey",
              "Sawbridgeworth", "Hatfield Heath", "Matching Tye",
            ].map((town) => {
              const area = AREAS.find((a) => a.name === town);
              const to = area ? `/live-in-care/${area.slug}` : "/live-in-care";
              return (
                <li key={town}>
                  <Link
                    to={to}
                    data-testid={`area-link-${area ? area.slug : town.toLowerCase().replace(/\s+/g, "-")}`}
                    className="px-4 py-2 rounded-full border border-[var(--ss-cream)]/20 text-sm bg-[var(--ss-teal-900)]/40 transition-colors hover:border-[var(--ss-gold)] hover:bg-[var(--ss-teal-900)]/60 inline-flex items-center gap-2 cursor-pointer"
                  >
                    {town}
                    <i className="fa-solid fa-arrow-right text-[10px] text-[var(--ss-gold)]"></i>
                  </Link>
                </li>
              );
            })}
          </ul>
          <p className="mt-8 text-sm text-[var(--ss-cream)]/60">
            Not sure if we cover your postcode? Call <a href={SITE.phoneHref} className="underline hover:text-[var(--ss-gold)]">{SITE.phone}</a> and we'll tell you straight away.
          </p>
        </div>
      </section>

      {/* CTA */}
      <CTABanner />
    </main>
  );
};

export default HomePage;
